import { Badge, type BadgeTone } from '@/components/ui';
import { lineProjectStatusLabel } from '@/lib/line-project-status';
import type { LineProjectStatus } from '@/lib/types';

/**
 * The "Projects" cell on the quote and invoice lists — whether a document's LINE
 * ITEMS have been given a project.
 *
 * ⚠️ **ONE COMPONENT, FOUR MOUNT POINTS** — the desktop cell and the sub-`md`
 * stacked card on both `/quotes` and `/invoices`. The two layouts are the SAME
 * list at two widths, and a column that exists on one of them is how a person on
 * a phone concludes a document carries no state at all. Same reasoning as
 * `ImportedFromXeroBadge`.
 *
 * The WORDS are in `lib/line-project-status.ts`, with the filter options that
 * have to say the same thing; read its header for why the question has four
 * answers rather than the two it was asked with.
 *
 * ══ WHY NONE OF THE TONES IS `danger` ═════════════════════════════════════
 *
 * ⚠️ **A document without projects is NOT broken and must not look broken.**
 * Every Xero-imported document is in that state — the importer writes line items
 * and never writes a project — so on `/invoices` this is the ordinary majority
 * answer, not a fault. `danger` beside a real refusal would make the two
 * indistinguishable, and a tone that shouts on most rows teaches people to
 * ignore the tone. That is the same call `ImportedFromXeroBadge` records for the
 * same population.
 *
 * `warning` on `some` is the one tone that is earned: a partly-assigned document
 * is the state somebody actually has to go and finish.
 *
 * **Tone is never the only signal** — every branch carries its own words.
 *
 * ⛔ **No helper text.** The labels stand alone; nothing here is a refusal, a
 * disabled control or an error, so the standing prohibition applies with no
 * exception to claim. The `title` carries only what a truncated cell would
 * otherwise lose.
 */
/*
 * 🔴 **`warning` ON `some` BECAME EARNED ON 2026-09-02, HAVING NOT BEEN BEFORE —
 * and the comment above claiming it was earned was wrong when it was written.**
 *
 * Until today these states counted EVERY line. Measured against production: the
 * column flagged **931 documents where only 136 had a real gap**, and on `some`
 * specifically the amber badge was wrong **85% of the time** — 42 of 50 quotes
 * and 64 of 75 invoices in that bucket were already complete, every remaining
 * projectless line sitting on a product not sold through projects at all. Those
 * documents would have worn a warning for ever, which is precisely the "teaches
 * people to ignore the tone" failure this file's own header warns about two
 * paragraphs earlier.
 *
 * The server now counts only lines that CAN carry a project, so `some` really
 * does mean half-finished and the tone is earned for the first time. **The word
 * did not change; what it counts did.**
 */
const TONES: Record<LineProjectStatus, BadgeTone> = {
  // Nothing to do here, and nothing wrong: the quietest tone in the set.
  no_lines: 'neutral',
  // The ordinary answer on most imported documents. Deliberately not `danger`.
  none: 'neutral',
  // The only state that is genuinely half-finished, and the only earned tone.
  some: 'warning',
  all: 'success',
  /*
   * ⚠️ `neutral`, and deliberately NOT `success`. Nothing is outstanding, but
   * nothing was achieved either — no project was assigned, because none could
   * be. A green tick would credit somebody with work that never existed, and on
   * a majority of imported documents that reads as the column congratulating the
   * table.
   */
  no_projectable_lines: 'neutral',
};

export function LineProjectStatusCell({ status }: { status: LineProjectStatus }) {
  /*
   * ⚠️ **RENDERS NOTHING RATHER THAN AN EMPTY BADGE WHEN THE FIELD IS ABSENT,
   * and the absent case is a real deploy window, not a paranoid one.**
   *
   * The API must ship this field BEFORE this client does — that is the standing
   * ordering rule, and it is the direction that cannot be got wrong safely. But
   * a browser holding a cached bundle can outlive a rollback, and `status` is
   * then `undefined` while `tsc` insists it cannot be: exactly the shape this
   * codebase has on record from `f_docusign_envelopes`.
   *
   * An empty badge would read as a FIFTH state — a document whose projects are
   * "something we have no word for" — which is a claim. Nothing is the honest
   * render: the column is blank, which reads as "not answered here", and the
   * cell beside it still identifies the row.
   *
   * ⚠️ Deliberately NOT defaulted to `none`. That would print "Not added" over a
   * document whose lines all name a project, on the strength of a field that
   * never arrived — a confident wrong answer, which is the one outcome worse
   * than a blank.
   */
  if (status === undefined || status === null) return null;

  const label = lineProjectStatusLabel(status);
  /*
   * An unrecognised value keeps the neutral tone and prints itself verbatim (see
   * `lineProjectStatusLabel`): a member newer than this bundle is not an error,
   * and colouring it as one would make a future state look broken.
   */
  return (
    <Badge tone={TONES[status] ?? 'neutral'} title={label}>
      {label}
    </Badge>
  );
}
